/**
 * WhatsApp entry in the Gorgias chat
 *
 * Gorgias has WhatsApp enabled as a helpdesk channel but no shopper-facing entry point for
 * it, so this adds one to the chat's home view, directly above the flow buttons.
 *
 * The chat window is a same-origin srcdoc iframe (#chat-window) rendered by React. It
 * re-renders the home view whenever the shopper moves around inside the chat. The previous
 * version injected once and stopped watching, so the entry vanished on the next render. It
 * also took its styling from whichever button it found first — sometimes a hidden alert's
 * dismiss button — so it moved and changed shape from one load to the next. This version:
 *
 * - owns its styling outright, so it looks the same on every render;
 * - has one anchor, directly below the home header and so above the flow buttons, and never
 *   renders outside the home view;
 * - re-checks on every DOM mutation inside the chat, so a re-render cannot remove it for
 *   longer than a frame;
 * - re-checks once a second from this page, with no cut-off, which picks up Gorgias loading
 *   late, the iframe being created, and its document being replaced;
 * - is a real link, which Safari always lets open in a new tab.
 *
 * Config comes from #custom-gorgias-whatsapp-config, rendered by custom-gorgias-whatsapp.liquid.
 */
(() => {
  if (window.customGorgiasWhatsApp) return;
  window.customGorgiasWhatsApp = true;

  let config;
  try {
    config = JSON.parse(document.getElementById('custom-gorgias-whatsapp-config')?.textContent || '');
  } catch (e) {
    return;
  }

  const phoneNumber = String(config.phoneNumber || '').replace(/\D/g, '');
  if (!phoneNumber) return;

  const href = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(config.message || '')}`;
  const title = config.title || 'WhatsApp';
  const subtitle = config.subtitle || '';

  const TILE_ID = 'custom-whatsapp-tile';
  const STYLE_ID = 'custom-whatsapp-tile-css';
  const MAX_MOVES_PER_SECOND = 10;
  let movesThisSecond = 0;

  // Mirrors Gorgias' flow buttons (translucent pill, Inter via inherit) with fixed values, so
  // nothing about the tile depends on what else happens to be rendered.
  const CSS = `
    #${TILE_ID} {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      box-sizing: border-box;
      max-width: calc(100% - 8px);
      margin: 0 4px; /* the flow buttons' block is padded 4px; this lines the tile up with them */
      padding: 8px 20px 8px 10px;
      border: 0.5px solid rgba(255, 255, 255, 0.3);
      border-radius: 40px;
      background: rgba(255, 255, 255, 0.55);
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.05);
      -webkit-backdrop-filter: blur(3px);
      backdrop-filter: blur(3px);
      color: rgb(30, 36, 46);
      font-family: inherit;
      text-align: left;
      text-decoration: none;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: background-color 0.15s ease;
    }
    #${TILE_ID}:hover { background: rgba(255, 255, 255, 0.75); }
    #${TILE_ID}:focus-visible { outline: 2px solid #25d366; outline-offset: 2px; }
    #${TILE_ID} .custom-whatsapp-tile__icon { flex: 0 0 28px; width: 28px; height: 28px; display: block; }
    #${TILE_ID} .custom-whatsapp-tile__text { display: flex; flex-direction: column; min-width: 0; }
    #${TILE_ID} .custom-whatsapp-tile__title { font-size: 14px; line-height: 18px; font-weight: 600; }
    #${TILE_ID} .custom-whatsapp-tile__subtitle { font-size: 12px; line-height: 16px; font-weight: 400; opacity: 0.75; }
  `;

  const ICON = `
    <svg class="custom-whatsapp-tile__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path fill="#25D366" d="M12 2a10 10 0 00-8.66 15l-1.2 4.4 4.52-1.18A10 10 0 1012 2z"/>
      <path fill="#fff" d="M16.04 13.63c-.24-.12-1.4-.69-1.62-.77-.22-.08-.38-.12-.54.12-.16.24-.61.77-.74.93-.14.16-.27.18-.5.06-.23-.12-.97-.37-1.85-1.14-.68-.61-1.15-1.36-1.28-1.59-.13-.23-.01-.35.1-.47.1-.1.23-.27.34-.4.11-.13.15-.23.23-.39.08-.16.04-.29-.02-.41-.06-.12-.52-1.24-.72-1.7-.19-.45-.38-.39-.52-.4-.13 0-.29-.01-.45-.01s-.41.06-.63.29c-.22.23-.83.79-.83 1.94 0 1.15.85 2.26.97 2.42.12.16 1.68 2.52 4.08 3.53.57.24 1.02.38 1.37.49.58.18 1.09.15 1.5.09.46-.07 1.41-.57 1.61-1.11.2-.55.2-1.01.14-1.11-.06-.1-.22-.16-.46-.28z"/>
    </svg>
  `;

  const buildTile = (doc) => {
    const tile = doc.createElement('a');
    tile.id = TILE_ID;
    tile.href = href;
    tile.target = '_blank';
    tile.rel = 'noopener noreferrer';
    tile.setAttribute('aria-label', subtitle ? `${title}: ${subtitle}` : title);
    tile.innerHTML = `${ICON}<span class="custom-whatsapp-tile__text"><span class="custom-whatsapp-tile__title"></span><span class="custom-whatsapp-tile__subtitle"></span></span>`;
    tile.querySelector('.custom-whatsapp-tile__title').textContent = title;
    const subtitleEl = tile.querySelector('.custom-whatsapp-tile__subtitle');
    if (subtitle) subtitleEl.textContent = subtitle;
    else subtitleEl.remove();
    return tile;
  };

  /**
   * Where the tile belongs in this render, or null when the home view is not showing.
   *
   * The home view is a column of #homepage-header followed by the block of flow buttons, on
   * desktop and mobile alike, so directly after the header is directly above the flows. The
   * header's id is the one stable hook in Gorgias' markup; its classes are hashed, and even
   * the readable ones differ by breakpoint.
   */
  const findSlot = (doc) => {
    const header = doc.getElementById('homepage-header');
    if (!header?.parentElement) return null;

    // Measured past the tile itself: once it sits here, header.nextSibling *is* the tile, and
    // comparing against that would call it misplaced forever.
    let before = header.nextSibling;
    while (before && before.id === TILE_ID) before = before.nextSibling;
    return { parent: header.parentElement, before };
  };

  const ensureTile = (doc) => {
    const slot = findSlot(doc);
    if (!slot?.parent) return;

    if (!doc.getElementById(STYLE_ID)) {
      const style = doc.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      (doc.head || doc.documentElement).appendChild(style);
    }

    const tile = doc.getElementById(TILE_ID) || buildTile(doc);
    const inPlace = tile.parentElement === slot.parent && tile.nextSibling === slot.before;
    if (inPlace) return;

    // Every move is itself a mutation that runs this again. Placement is meant to settle in
    // one move; the cap means a future Gorgias markup change can only leave the tile out of
    // place, never lock the page in a move loop. It resets on each once-a-second check.
    if (movesThisSecond >= MAX_MOVES_PER_SECOND) return;
    movesThisSecond += 1;
    slot.parent.insertBefore(tile, slot.before);
  };

  const observed = new WeakSet();

  const check = () => {
    movesThisSecond = 0;
    if (document.hidden) return;

    let doc = null;
    try {
      doc = document.getElementById('chat-window')?.contentDocument || null;
    } catch (e) {
      return;
    }
    if (!doc?.documentElement) return;

    if (!observed.has(doc)) {
      observed.add(doc);
      new MutationObserver(() => ensureTile(doc)).observe(doc, { childList: true, subtree: true });
    }

    ensureTile(doc);
  };

  check();
  setInterval(check, 1000);
})();